/* "Va tutto bene?" — la domanda a cui la dashboard risponde per prima.
 *
 * Il calcolo sta qui e non nella schermata perché è l'unica logica della
 * dashboard che cambia una decisione: se sbaglia, uno guarda un pannello verde
 * mentre un bot con denaro reale ha smesso di respirare.
 *
 * ## Perché «fermo con denaro reale» non è più una segnalazione
 *
 * Lo era, e produceva una scheda per ogni bot fermo: con nove bot spenti a mano
 * la dashboard si apriva su una parete di nove riquadri identici, tutti con la
 * stessa frase, e la tessera in alto annunciava «9 bot chiedono attenzione».
 * Nessuno dei nove chiedeva niente: erano stati spenti apposta, la condizione è
 * permanente e il proprietario la conosce. Un elenco di cose da guardare che
 * contiene per lo più cose già viste e non azionabili smette di essere letto, e
 * il giorno in cui contiene un guasto vero quel guasto è la decima riga uguale
 * alle altre nove.
 *
 * Resta detto — la configurazione a denaro reale su un bot spento è un fatto —
 * ma in UNA riga di riepilogo, non in nove schede.
 *
 * ## Cosa è azionabile davvero
 *
 * 1. un bot che risulta IN MARCIA e non dà segni di vita (`vitalitaDi`): è
 *    l'unica condizione che nasce da sola, di notte, senza che nessuno la
 *    decida — ed è quella che costa, perché il denaro è a mercato e nessuno lo
 *    sorveglia. Prima non esisteva affatto in questo elenco;
 * 2. gli errori registrati dal processo;
 * 3. le transazioni non andate a buon fine.
 *
 * Non esiste un endpoint di stato dei bot: questi segnali sono tutto quello che
 * le colonne dell'entity permettono di dedurre. Il limite è dichiarato in
 * RICHIESTE-API.md, punto 6.
 */

import type { Tradebot } from '../../api/tipi';
import { vitalitaDi } from '../../lib/vitalita';

export type MotivoAttenzione = 'battito-fermo' | 'errore' | 'transazioni';

export interface Segnalazione {
  bot: Tradebot;
  motivo: MotivoAttenzione;
  /** Frase pronta da mostrare accanto al nome del bot. */
  testo: string;
  /** Più basso = più grave. Ordina l'elenco, e non si legge a schermo. */
  gravita: number;
}

export interface Salute {
  attivi: number;
  fermi: number;
  /** In marcia e visti di recente: quelli su cui i numeri della pagina sono di adesso. */
  vivi: number;
  /** Solo cose azionabili, dalla più grave. */
  segnalazioni: Segnalazione[];
  /** I bot fermi ma configurati con denaro reale. Condizione nota e permanente:
   *  vale una riga di riepilogo, non una scheda per bot. */
  fermiConDenaroReale: Tradebot[];
}

/* Il denaro vero pesa meno di un gradino: fra due guasti dello stesso tipo
   viene prima quello che muove soldi, ma un battito fermo su un bot simulato
   resta più grave di una transazione fallita su uno reale. */
const PESO: Record<MotivoAttenzione, number> = {
  'battito-fermo': 0,
  errore: 2,
  transazioni: 4,
};

function gravita(motivo: MotivoAttenzione, bot: Tradebot): number {
  return PESO[motivo] + (bot.useRealMoney ? 0 : 1);
}

export function leggiSalute(bots: Tradebot[], adesso = Date.now()): Salute {
  const segnalazioni: Segnalazione[] = [];
  const fermiConDenaroReale: Tradebot[] = [];
  let vivi = 0;

  for (const b of bots) {
    const v = vitalitaDi(b, adesso);
    if (v.stato === 'vivo') vivi += 1;

    if (!b.isRunning && b.useRealMoney) fermiConDenaroReale.push(b);

    /* Un bot per volta compare una volta sola, col suo motivo più grave: due
       righe sullo stesso bot si leggono come due guasti. */

    /* PRIMA di tutto: risulta in marcia e non respira. Non l'ha deciso nessuno,
       è successo — ed è l'unica delle tre condizioni che nessuno ha già visto. */
    if (v.allarme) {
      segnalazioni.push({
        bot: b,
        motivo: 'battito-fermo',
        testo: b.useRealMoney
          ? `${v.testo} · qui c'è denaro vero a mercato`
          : v.testo,
        gravita: gravita('battito-fermo', b),
      });
      continue;
    }

    /* `errors` è un array di stringhe senza data né ordine: si può dire quanti
       sono, non quando sono successi. */
    if (b.errors && b.errors.length > 0) {
      segnalazioni.push({
        bot: b,
        motivo: 'errore',
        testo:
          b.errors.length === 1
            ? `1 errore registrato: ${b.errors[0]}`
            : `${b.errors.length} errori registrati, l'ultimo: ${b.errors[b.errors.length - 1]}`,
        gravita: gravita('errore', b),
      });
      continue;
    }

    if ((b.failedTransactions ?? 0) > 0) {
      segnalazioni.push({
        bot: b,
        motivo: 'transazioni',
        testo: `${b.failedTransactions} transazioni non andate a buon fine`,
        gravita: gravita('transazioni', b),
      });
    }
  }

  segnalazioni.sort((a, b) => a.gravita - b.gravita || b.bot.id - a.bot.id);

  return {
    attivi: bots.filter((b) => b.isRunning).length,
    fermi: bots.filter((b) => !b.isRunning).length,
    vivi,
    segnalazioni,
    fermiConDenaroReale,
  };
}

/**
 * La riga sola che ha preso il posto delle nove schede.
 *
 * `null` quando non c'è niente da dire: una riga che scrive «0 bot fermi con
 * denaro reale» è esattamente il rumore che si stava togliendo.
 */
export function riassuntoFermiConDenaro(salute: Salute): string | null {
  const fermi = salute.fermiConDenaroReale;
  const n = fermi.length;
  if (n === 0) return null;

  if (n === 1) {
    const b = fermi[0];
    return `Il bot #${b.id} (${b.asset}/${b.currency}) è fermo ma configurato con denaro reale: non opera finché non lo si riavvia.`;
  }

  /* Gli identificativi bastano, e non tutti: sono il modo in cui i bot vengono
     chiamati nei messaggi Telegram e nei log. La coppia per ognuno allungherebbe
     la riga fino a farla tornare una parete, che è quello da cui si veniva. */
  const MAX = 6;
  const chi = fermi
    .slice(0, MAX)
    .map((b) => `#${b.id}`)
    .join(' ');
  const resto = n > MAX ? ` e altri ${n - MAX}` : '';
  return `${n} bot fermi sono configurati con denaro reale (${chi}${resto}): non operano finché non li si riavvia.`;
}

/** Quante operazioni ha fatto un bot. `trades` è una relazione eager: arriva
 *  comunque nella risposta, quindi contarla non costa una chiamata in più.
 *  Se il backend un giorno smettesse di mandarla, il valore diventa `null` e la
 *  terna mostra `n/d` — mai zero, che si leggerebbe come "non ha mai operato". */
export function operazioniDi(b: Tradebot): number | null {
  return Array.isArray(b.trades) ? b.trades.length : null;
}
